import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Bell, Check, CheckCheck, Trash2, RefreshCw,
  Users, Calendar, Link2, Eye, AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTIFICATION_ICONS = {
  new_application: Users,
  interview_confirmed: Calendar,
  referral_click: Link2,
  page_view: Eye,
  default: Bell
};

const NOTIFICATION_COLORS = {
  new_application: 'bg-blue-100 text-blue-600',
  interview_confirmed: 'bg-green-100 text-green-600',
  referral_click: 'bg-purple-100 text-purple-600',
  page_view: 'bg-yellow-100 text-yellow-600',
  default: 'bg-gray-100 text-gray-600'
};

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (error) {
      toast.error('Failed to update notification');
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to update notifications');
    }
  };

  const deleteNotification = async (id) => {
    try {
      await axios.delete(`${API}/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const clearReadNotifications = async () => {
    const readCount = notifications.filter(n => n.read).length;
    if (!window.confirm(`Delete ${readCount} read notification${readCount > 1 ? 's' : ''}?`)) return;
    try {
      const res = await axios.delete(`${API}/notifications`);
      setNotifications(prev => prev.filter(n => !n.read));
      toast.success(`Cleared ${res.data.deleted_count} old notifications`);
    } catch (error) {
      toast.error('Failed to clear notifications');
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const getIcon = (type) => {
    const Icon = NOTIFICATION_ICONS[type] || NOTIFICATION_ICONS.default;
    return Icon;
  };

  const getIconColor = (type) => {
    return NOTIFICATION_COLORS[type] || NOTIFICATION_COLORS.default;
  };

  return (
    <div className="p-6 space-y-6" data-testid="notifications-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#4B2D6E] font-[Lato]">Notifications</h1>
          <p className="text-[#666666] mt-1">
            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchNotifications} variant="outline" className="border-[#4B2D6E] text-[#4B2D6E]">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          {unreadCount > 0 && (
            <Button onClick={markAllRead} className="bg-[#E8722A] hover:bg-[#D6621F]" data-testid="mark-all-read-btn">
              <CheckCheck className="mr-2 h-4 w-4" />
              Mark All Read
            </Button>
          )}
          {notifications.filter(n => n.read).length > 0 && (
            <Button onClick={clearReadNotifications} variant="outline" className="border-red-400 text-red-500 hover:bg-red-50" data-testid="clear-read-btn">
              <Trash2 className="mr-2 h-4 w-4" />
              Clear Read
            </Button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#4B2D6E] border-t-transparent mx-auto"></div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-12 text-center text-[#666666]">
              <Bell className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No notifications yet</p>
              <p className="text-sm mt-1">You'll see updates about applications and referrals here</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => {
                const Icon = getIcon(notification.type);
                const iconColor = getIconColor(notification.type);
                
                return (
                  <div 
                    key={notification.id}
                    className={`p-4 flex items-start gap-4 hover:bg-[#F5F5F5] transition-colors ${
                      !notification.read ? 'bg-[#E8722A]/5' : ''
                    }`}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${iconColor}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className={`text-[#333333] ${!notification.read ? 'font-semibold' : ''}`}>
                            {notification.message}
                          </p>
                          <p className="text-sm text-[#666666] mt-1">
                            {new Date(notification.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {!notification.read && (
                            <Badge className="bg-[#E8722A] text-white text-xs">New</Badge>
                          )}
                          {notification.data?.candidate_id && (
                            <Link to={`/dashboard/candidates/${notification.data.candidate_id}`}>
                              <Button variant="outline" size="sm">View</Button>
                            </Link>
                          )}
                          {!notification.read && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => markAsRead(notification.id)}
                              data-testid={`mark-read-${notification.id}`}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteNotification(notification.id)}
                            className="text-red-400 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-notification-${notification.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationsPage;
